import React from "react";
import { ToolbarAvoid } from "@autodromo/contextual-fields";
import type { DynamicStructure } from "../../../utils/interpolate";
import { resolveMegaMenuCards, resolveMegaMenuSize } from "../logic";
import { STRINGS } from "../strings";
import { MegaMenuGrid } from "./MegaMenuGrid";
import { MegaMenuSeeAllLink } from "./MegaMenuSeeAllLink";
import { MegaMenuInner, MegaMenuPanel as Panel } from "../HeaderV2.styles";
import {
  ButtonVariant,
  IconAlignment,
  MegaMenuAlign,
  MegaMenuCardAlign,
  MegaMenuCardLayout,
  MegaMenuDensity,
  type HeaderV2Link,
  type MegaMenuConfig,
  type MegaMenuLinkData,
} from "../types";

type MegaMenuPanelProps = {
  id: string;
  link: HeaderV2Link;
  data: MegaMenuLinkData | undefined;
  structure?: DynamicStructure;
  config: MegaMenuConfig;
  columns: number;
  htmlId?: string;
  onNavigate: () => void;
};

export const MegaMenuPanel = ({ id, link, data, structure, config, columns, htmlId, onNavigate }: MegaMenuPanelProps) => {
  const item = link.megaMenu;
  const cardCtaLabel = item?.cardCtaLabel ?? STRINGS.cardCtaDefault;
  const size = resolveMegaMenuSize({ width: config.width, height: config.height });
  const density = config.density ?? MegaMenuDensity.COMFORTABLE;
  const cards = resolveMegaMenuCards({ entries: data?.entries ?? [], structure, item: link.megaMenu });

  return (
    <Panel
      id={id}
      role="region"
      aria-label={STRINGS.megaMenuPanel(link.name)}
      $fullscreen={size.fullHeight}
      data-testid="header-v2-megamenu"
      data-toolbar-avoid={ToolbarAvoid.CONTENT}
    >
      <MegaMenuInner $width={size.width} $align={config.align ?? MegaMenuAlign.CENTER} $density={density}>
        <MegaMenuGrid
          status={data?.status}
          cards={cards}
          columns={columns}
          density={density}
          aspect={config.aspect}
          layout={config.cardLayout ?? MegaMenuCardLayout.ABOVE}
          ctaLabel={item?.showCardCta && cardCtaLabel.trim() ? cardCtaLabel : undefined}
          ctaVariant={item?.cardCtaVariant ?? ButtonVariant.LINK}
          ctaShowIcon={item?.cardCtaShowIcon !== false}
          ctaIcon={item?.cardCtaIcon}
          ctaIconAlignment={item?.cardCtaIconAlignment ?? IconAlignment.RIGHT}
          cardAlign={config.cardAlign ?? MegaMenuCardAlign.START}
          htmlId={htmlId}
          onNavigate={onNavigate}
        />

        <MegaMenuSeeAllLink link={link} structure={structure} onNavigate={onNavigate} />
      </MegaMenuInner>
    </Panel>
  );
};
